import { useState, useEffect, useRef, useCallback } from 'react';

const DISMISS_KEY = 'zno:installPromptDismissed';
const INSTALLED_KEY = 'zno:pwaInstalled';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY = 5000;
const ANDROID_FALLBACK_DELAY = 8000;

function wasDismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return (Date.now() - Number(ts)) < DISMISS_DURATION;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // no-op
  }
}

function wasInstalledBefore() {
  try {
    return localStorage.getItem(INSTALLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, 'true');
  } catch {
    // no-op
  }
}

function detectIsStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function detectIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
  return isIOS && isSafari;
}

function detectAndroidChrome() {
  const ua = navigator.userAgent;
  return /Android/i.test(ua) && /Chrome/i.test(ua) && !/OPR/i.test(ua);
}

export function useInstallPrompt() {
  const [platform, setPlatform] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(() => detectIsStandalone() || wasInstalledBefore());
  const deferredPromptRef = useRef(null);

  // Detect iOS Safari immediately
  useEffect(() => {
    if (isInstalled) return;
    if (detectIOSSafari()) {
      setPlatform('ios');
    }
  }, [isInstalled]);

  // Intercept beforeinstallprompt (Android/Chrome/Desktop)
  // Also check for early-captured event from index.html
  useEffect(() => {
    if (isInstalled) return;

    // Check if the event was already captured before React mounted
    if (window.__pwaInstallPrompt) {
      deferredPromptRef.current = window.__pwaInstallPrompt;
      window.__pwaInstallPrompt = null;
      const isMobile = /Android|webOS/i.test(navigator.userAgent);
      setPlatform(isMobile ? 'android' : 'desktop');
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      window.__pwaInstallPrompt = null;
      const isMobile = /Android|webOS/i.test(navigator.userAgent);
      setPlatform(isMobile ? 'android' : 'desktop');
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isInstalled]);

  // Fallback: if on Android Chrome and beforeinstallprompt hasn't fired,
  // show manual instructions after a longer delay
  useEffect(() => {
    if (isInstalled || platform) return;
    if (!detectAndroidChrome()) return;

    const timer = setTimeout(() => {
      if (!deferredPromptRef.current) {
        setPlatform('android-manual');
      }
    }, ANDROID_FALLBACK_DELAY);

    return () => clearTimeout(timer);
  }, [isInstalled, platform]);

  // Listen for appinstalled
  useEffect(() => {
    const handler = () => {
      setIsInstalled(true);
      markInstalled();
      setVisible(false);
    };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // Delayed appearance
  useEffect(() => {
    if (isInstalled || wasDismissedRecently() || !platform) return;

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY);
    return () => clearTimeout(timer);
  }, [platform, isInstalled]);

  const triggerInstall = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;
    prompt.prompt();
    const result = await prompt.userChoice;
    deferredPromptRef.current = null;
    // Mark as installed regardless of outcome to avoid re-prompting
    setIsInstalled(true);
    markInstalled();
    setVisible(false);
  }, []);

  const dismiss = useCallback(() => {
    setDismissed();
    setVisible(false);
  }, []);

  return {
    shouldShow: visible && !isInstalled && platform !== null,
    platform,
    isInstalled,
    triggerInstall,
    dismiss,
  };
}
